import { useDeleteUser, useFetchUser, usePatchUser } from '@api';
import { AdminModalType, ContactCountryCodeEnum, PathnameEnum, ToastMessageEnum, UserRoleEnum } from '@constant';
import { useSelector } from '@stores';
import { IPatchUserBody } from '@types';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useToast } from './useToast';

//어드민 - 회원정보생성 및 회원정보변경 페이지 로직에 관여
export const useAdminUserEdit = () => {
  const { userIdPathParam } = useParams<{ userIdPathParam: string }>();
  const userId = userIdPathParam;

  // 입력값 상태 관리
  const [formData, setFormData] = useState<{
    name: string;
    contact: string;
    countryCode: ContactCountryCodeEnum;
    userIps: { nickname: string; ip: string }[];
    role: UserRoleEnum;
    password?: string;
  }>({
    name: '',
    contact: '',
    countryCode: ContactCountryCodeEnum['대한민국 (+82)'],
    userIps: [{ nickname: '', ip: '' }], // 최초 한 줄의 빈 필드로 시작
    role: UserRoleEnum['일반 회원'],
    password: ''
  });
  const [passwordConfirm, setPasswordConfirm] = useState('');
  // 초기 데이터 저장 (변경사항 추적용)
  const [initialData, setInitialData] = useState<{
    name: string;
    contact: string;
    countryCode: ContactCountryCodeEnum;
    userIps: { nickname: string; ip: string }[];
    role: UserRoleEnum;
  } | null>(null);
  // 에러 상태 관리
  const [errors, setErrors] = useState({
    name: '',
    contact: '',
    userIps: [{ nickname: '', ip: '' }] as { nickname: string; ip: string }[],
    password: '',
    passwordConfirm: ''
  });
  const [modalType, setModalType] = useState<AdminModalType>(AdminModalType.USER_EDIT_CANCEL);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const { showToast } = useToast();

  const loginUserRole = useSelector((state) => state.auth.role);

  const navigate = useNavigate();

  // 필드별 ref 관리
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  const ipRefs = useRef<(HTMLInputElement | null)[]>([]);
  const passwordRef = useRef<HTMLInputElement>(null);
  const passwordConfirmRef = useRef<HTMLInputElement>(null);

  const { response } = useFetchUser(userId);

  // GET /users/:id 응답 데이터로 formData 설정
  useEffect(() => {
    if (response?.data) {
      const userData = response.data;
      const userIps = userData.userIps && userData.userIps.length > 0 ? userData.userIps : [{ nickname: '', ip: '' }];

      // contact 파싱: "+82 01012345678" 형식에서 countryCode와 contact 분리
      let parsedCountryCode = ContactCountryCodeEnum['대한민국 (+82)'];
      let parsedContact = userData.contact || '';

      if (userData.contact) {
        const spaceIndex = userData.contact.indexOf(' ');
        if (spaceIndex > 0) {
          const code = userData.contact.substring(0, spaceIndex);
          parsedContact = userData.contact.substring(spaceIndex + 1);
          // countryCode가 enum에 있는지 확인
          if (Object.values(ContactCountryCodeEnum).includes(code as ContactCountryCodeEnum)) {
            parsedCountryCode = code as ContactCountryCodeEnum;
          }
        }
      }

      setFormData({
        name: userData.name || '',
        contact: parsedContact,
        countryCode: parsedCountryCode,
        userIps: userIps,
        role: userData.role || UserRoleEnum['일반 회원']
      });

      // 초기 데이터 저장
      setInitialData({
        name: userData.name || '',
        contact: parsedContact,
        countryCode: parsedCountryCode,
        userIps: JSON.parse(JSON.stringify(userIps)), // 깊은 복사
        role: userData.role || UserRoleEnum['일반 회원']
      });

      // errors.userIps 배열의 길이를 formData.userIps와 동기화
      if (userData.userIps && userData.userIps.length > 0) {
        setErrors((prev) => ({
          ...prev,
          userIps: userData.userIps.map(() => ({ nickname: '', ip: '' }))
        }));
      }
    }
  }, [response]);

  const { mutateAsync: patchUserMutateAsync, isPending: isPatchUserPending } = usePatchUser();

  const { mutateAsync: deleteUserMutateAsync, isPending: isDeleteUserPending } = useDeleteUser();

  // 접근 권한 체크: 최고 관리자는 루트 관리자 또는 최고 관리자 편집 불가
  const canAccessEdit = () => {
    if (!response?.data) return true; // 데이터 로딩 중에는 ForbiddenElement 보이지 않기 위해 true 반환

    const targetUserRole = response.data.role;

    // 최고 관리자가 루트 관리자 또는 최고 관리자를 편집하려는 경우 차단
    if (loginUserRole === UserRoleEnum['최고 관리자']) {
      if (targetUserRole === UserRoleEnum['루트 관리자'] || targetUserRole === UserRoleEnum['최고 관리자']) {
        return false;
      }
    }

    return true;
  };

  const handleInputChange = (
    field: 'name' | 'contact' | 'countryCode' | 'userIps' | 'role' | 'password',
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
    // 입력 시 해당 필드의 에러 초기화
    if (field in errors) {
      setErrors((prev) => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handlePasswordConfirmChange = (value: string) => {
    setPasswordConfirm(value);
    // 비밀번호 확인 입력 시 에러 초기화
    setErrors((prev) => ({
      ...prev,
      passwordConfirm: ''
    }));
  };

  // 연락처 필드 값 변경 (숫자만 허용)
  const handleContactChange = (value: string) => {
    // 숫자만 허용 (빈 문자열 또는 숫자만 포함된 문자열)
    if (value === '' || /^\d+$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        contact: value
      }));
      // 입력 시 에러 초기화
      setErrors((prev) => ({
        ...prev,
        contact: ''
      }));
    }
    // 숫자가 아닌 문자가 포함되어 있으면 상태 업데이트를 하지 않음 (입력 무시)
  };

  // IP 필드 값 변경
  const handleIpChange = (index: number, field: 'nickname' | 'ip', value: string) => {
    setFormData((prev) => ({
      ...prev,
      userIps: prev.userIps.map((ip, i) => (i === index ? { ...ip, [field]: value } : ip))
    }));
    // 해당 IP 필드의 에러 초기화
    setErrors((prev) => ({
      ...prev,
      userIps: prev.userIps.map((ipError, i) => (i === index ? { ...ipError, [field]: '' } : ipError))
    }));
  };

  // IP 추가 (새로운 빈 줄 추가)
  const handleAddIp = () => {
    setFormData((prev) => ({
      ...prev,
      userIps: [...prev.userIps, { nickname: '', ip: '' }]
    }));
    // 에러 배열에도 빈 에러 추가
    setErrors((prev) => ({
      ...prev,
      userIps: [...prev.userIps, { nickname: '', ip: '' }]
    }));
  };

  // IP 삭제
  const handleRemoveIp = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      userIps: prev.userIps.filter((_, i) => i !== index)
    }));
    // 에러 배열에서도 해당 인덱스 제거
    setErrors((prev) => ({
      ...prev,
      userIps: prev.userIps.filter((_, i) => i !== index)
    }));
  };

  // IP 추가 버튼 활성화 조건: 모든 기존 IP가 채워져 있을 때
  const isAddIpButtonEnabled = formData.userIps.every((ip) => ip.nickname.trim() && ip.ip.trim());

  // 로그인한 유저의 role에 따라 roleOptions 분기
  const getRoleOptions = (fetchedRole?: UserRoleEnum) => {
    // 루트 관리자 유저의 정보를 변경하는 경우, 권한 Select에 '루트 관리자'를 보여주어야 함
    if (fetchedRole && fetchedRole === UserRoleEnum['루트 관리자']) {
      return [{ label: '루트 관리자', value: UserRoleEnum['루트 관리자'] }];
    }

    if (loginUserRole === UserRoleEnum['루트 관리자']) {
      // 루트 관리자인 경우: 최고 관리자, 데이터 관리자, 일반 회원
      return [
        { label: '최고 관리자', value: UserRoleEnum['최고 관리자'] },
        { label: '데이터 관리자', value: UserRoleEnum['데이터 관리자'] },
        { label: '일반 회원', value: UserRoleEnum['일반 회원'] }
      ];
    } else if (loginUserRole === UserRoleEnum['최고 관리자']) {
      // 최고 관리자인 경우: 데이터 관리자, 일반 회원
      return [
        { label: '데이터 관리자', value: UserRoleEnum['데이터 관리자'] },
        { label: '일반 회원', value: UserRoleEnum['일반 회원'] }
      ];
    }
    // 기본값 (다른 권한인 경우)
    return [];
  };

  // 에러 검사 및 첫 번째 에러 필드로 스크롤
  const validateAndScrollToError = (): boolean => {
    // 에러 초기화
    const newErrors = {
      name: '',
      contact: '',
      userIps: formData.userIps.map(() => ({ nickname: '', ip: '' })),
      password: '',
      passwordConfirm: ''
    };

    let hasError = false;

    // 유효성 검사
    if (!formData.name.trim()) {
      newErrors.name = '이름을 입력해주세요.';
      hasError = true;
    }

    if (!formData.contact.trim()) {
      newErrors.contact = '9~15자로 입력해주세요.';
      hasError = true;
    } else if (!/^\d+$/.test(formData.contact)) {
      newErrors.contact = '숫자만 입력해주세요.';
      hasError = true;
    } else if ((formData.contact.trim() && formData.contact.length < 9) || formData.contact.length > 15) {
      newErrors.contact = '9자 이상~15자 이하로 입력해주세요.';
      hasError = true;
    }

    // IP 필드 검증
    formData.userIps.forEach((ip, index) => {
      if (!ip.nickname.trim()) {
        newErrors.userIps[index].nickname = 'IP명을 입력해주세요.';
        hasError = true;
      }
      if (!ip.ip.trim()) {
        newErrors.userIps[index].ip = 'IP를 입력해주세요.';
        hasError = true;
      }
    });

    // 비밀번호 검증: 둘 다 비어있으면 OK, 입력했으면 4-20자 + 일치 체크
    const passwordEntered = formData.password || passwordConfirm;

    if (passwordEntered) {
      if (!formData.password || formData.password.length < 4 || formData.password.length > 20) {
        newErrors.password = '4~20자로 입력해 주세요.';
        hasError = true;
      }

      if (!passwordConfirm.trim()) {
        newErrors.passwordConfirm = '비밀번호를 입력해주세요.';
        hasError = true;
      } else if (formData.password !== passwordConfirm) {
        newErrors.passwordConfirm = '비밀번호가 일치하지 않습니다.';
        hasError = true;
      }
    }

    // 에러 상태 업데이트
    setErrors(newErrors);

    // 에러가 있으면 첫 번째 에러 필드로 스크롤
    if (hasError) {
      scrollToFirstError(newErrors);
    }

    return hasError;
  };

  // 첫 번째 에러 필드로 스크롤
  const scrollToFirstError = (newErrors: typeof errors) => {
    // 스크롤 함수 (컨테이너 상단에 정확히 위치)
    const scrollToElementWithOffset = (element: HTMLElement | null) => {
      if (element && scrollContainerRef.current) {
        let targetElement = element;

        // HTMLInputElement인 경우, 조상 중 TextInput의 Box를 찾기
        if (element instanceof HTMLInputElement) {
          // input -> TextField wrapper -> Box
          const boxElement = element.parentElement?.parentElement?.parentElement;
          if (boxElement) {
            targetElement = boxElement;
          }
        }
        // div 등 다른 요소인 경우 해당 요소를 직접 사용

        const containerRect = scrollContainerRef.current.getBoundingClientRect();
        const inputGap = 24; // 인풋과 인풋 사이의 간격
        const targetRect = targetElement.getBoundingClientRect();
        const relativeTop = targetRect.top - containerRect.top;
        const scrollTop = scrollContainerRef.current.scrollTop;
        const targetScrollTop = scrollTop + relativeTop - inputGap;

        scrollContainerRef.current.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth'
        });
      }
    };

    // 첫 번째 에러 필드 찾기 (페이지 렌더링 순서대로)
    if (newErrors.name && nameRef.current) {
      scrollToElementWithOffset(nameRef.current);
    } else if (newErrors.contact && contactRef.current) {
      scrollToElementWithOffset(contactRef.current);
    } else if (newErrors.userIps.some((ip) => ip.nickname || ip.ip)) {
      // IP 필드에서 첫 번째 에러 찾기
      for (let i = 0; i < newErrors.userIps.length; i++) {
        if (newErrors.userIps[i].nickname && ipRefs.current[i * 2]) {
          scrollToElementWithOffset(ipRefs.current[i * 2]);
          break;
        } else if (newErrors.userIps[i].ip && ipRefs.current[i * 2 + 1]) {
          scrollToElementWithOffset(ipRefs.current[i * 2 + 1]);
          break;
        }
      }
    } else if (newErrors.password && passwordRef.current) {
      scrollToElementWithOffset(passwordRef.current);
    } else if (newErrors.passwordConfirm && passwordConfirmRef.current) {
      scrollToElementWithOffset(passwordConfirmRef.current);
    }
  };

  // PATCH 요청 body 생성
  const createPatchBody = (): IPatchUserBody => {
    const body: IPatchUserBody = {};

    // name 변경 확인
    if (initialData && formData.name.trim() !== initialData.name) {
      body.name = formData.name.trim();
    }

    // contact 또는 countryCode 변경 확인
    if (initialData && (formData.contact !== initialData.contact || formData.countryCode !== initialData.countryCode)) {
      body.contact = `${formData.countryCode} ${formData.contact.trim()}`;
    }

    // role 변경 확인
    if (initialData && formData.role !== initialData.role) {
      body.role = formData.role;
    }

    // userIps 변경 확인 (추가/삭제/수정, 순서 변경은 무시)
    const isUserIpsChanged =
      initialData &&
      (formData.userIps.length !== initialData.userIps.length ||
        !formData.userIps.every((ip) =>
          initialData.userIps.some((initialIp) => initialIp.nickname === ip.nickname && initialIp.ip === ip.ip)
        ));
    if (isUserIpsChanged) {
      body.userIps = formData.userIps.map((ip) => ({
        nickname: ip.nickname.trim(),
        ip: ip.ip.trim()
      }));
    }

    // password 입력 확인
    if (formData.password && passwordConfirm) {
      body.password = formData.password.trim();
    }

    return body;
  };

  // PATCH 요청 전송
  const submitPatchRequest = async (body: IPatchUserBody) => {
    // userId가 undefined면 요청 보내지 않음
    if (!userId) {
      console.log('userId가 없습니다.');
      return;
    }

    // body가 비어있으면 요청 보내지 않음
    if (Object.keys(body).length === 0) {
      console.log('변경사항이 없습니다.');
      return;
    }

    try {
      await patchUserMutateAsync({
        body: body,
        userId: userId
      }).then(() => {
        setModalType(AdminModalType.USER_UPSERT_SUCCESS);
        setIsModalVisible(true);
      });
    } catch (error) {
      console.error('Failed to edit a user. Error: ', error);
      setModalType(AdminModalType.USER_EDIT_FAIL);
      setIsModalVisible(true);
    }
  };

  const handleSubmit = async () => {
    // 1. 유효성 검사 및 에러 시 스크롤
    const hasError = validateAndScrollToError();
    if (hasError) return;

    // 2. PATCH 요청 body 생성
    const body = createPatchBody();

    // 3. PATCH 요청 전송
    await submitPatchRequest(body);
  };

  const handleCancel = () => {
    setModalType(AdminModalType.USER_EDIT_CANCEL);
    setIsModalVisible(true);
  };

  const handleDeleteButtonClick = () => {
    setModalType(AdminModalType.USER_DELETE_CONFIRM);
    setIsModalVisible(true);
  };

  const handleModalConfirmClick = async () => {
    if (modalType === AdminModalType.USER_EDIT_CANCEL || modalType === AdminModalType.USER_UPSERT_SUCCESS) {
      navigate(PathnameEnum.ADMIN_USER_LIST_PAGE);
    } else if (modalType === AdminModalType.USER_EDIT_FAIL) {
      setIsModalVisible(false);
    } else if (modalType === AdminModalType.USER_DELETE_CONFIRM && userId) {
      await deleteUserMutateAsync(userId)
        .then(() => {
          setIsModalVisible(false);
          showToast(ToastMessageEnum.USER_DELETE_SUCCESS);
          navigate(PathnameEnum.ADMIN_USER_LIST_PAGE);
        })
        .catch(() => {
          setModalType(AdminModalType.USER_DELETE_FAIL);
        });
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  return {
    loginUserRole,

    formData,
    passwordConfirm,
    response,
    canAccessEdit: canAccessEdit(),

    handleInputChange,
    handlePasswordConfirmChange,
    handleContactChange,
    handleIpChange,
    handleAddIp,
    handleRemoveIp,
    isAddIpButtonEnabled,
    getRoleOptions,

    errors,
    scrollContainerRef,
    nameRef,
    contactRef,
    ipRefs,
    passwordRef,
    passwordConfirmRef,

    handleSubmit,

    handleCancel,
    handleDeleteButtonClick,
    isPatchUserPending,
    isDeleteUserPending,
    modalType,
    isModalVisible,
    handleModalConfirmClick,
    handleModalClose
  };
};
